import { useEffect, useMemo } from "react";
import { useIsMobileLayout } from "../../../hooks/useMediaQuery";
import { QuotationCostPricingMobile } from "../mobile-pages/QuotationCostPricingMobile";
import {
  sq,
  localizedFieldProps,
  formatInputNumber,
  supportsInstallation,
  parseCurrency,
  formatCurrency,
  pickFiniteNumber,
  resolveBackendRealizedSellingTotal,
  hasBackendPricingMetrics,
  roundExcelWhole,
  formatCurrencyWhole,
  normalizePmcEntry,
  patchPmcEntryValue,
  groupPmcEntriesForDisplay,
  isDiscountPercentAdjustment,
  computePreparedWorkingRow,
  computeWorkingTotals,
  CLEARED_BACKEND_AMOUNT_FIELDS,
  computeDerivedMultiplier,
  multipliersApproximatelyEqual,
  multiplierValueFollowsCostPricing,
  formatMultiplierFieldValue,
} from "./quotationUtils";
import { SectionShell, SummaryRow } from "./QuotationComponents";


function CostPricingTab({
  quotationType,
  rows,
  setRows,
  editable,
  pmcEntries = [],
  setPmcEntries,
  hasPmcConfigs = false,
  overheadsPercent = "0",
  setOverheadsPercent,
  markUpPercent = "0",
  setMarkUpPercent,
  netPricePercentage = 0,
  backendPricing = null,
  preferBackendPricing = false,
  forceBackendSheetCosts = false,
}) {
  const totals = computeWorkingTotals(rows, quotationType);
  const normalizedPmcEntries = pmcEntries.map((entry, index) => normalizePmcEntry(entry, index));
  const pmcGroups = groupPmcEntriesForDisplay(normalizedPmcEntries);
  const pmcTotal = pmcGroups.reduce((sum, head) => sum + (Number(head.totalCost) || 0), 0);
  // Cost basis = Working Sheet Initial/Input amounts (not selling amounts), per PRD.
  // When systems are lazy-loaded, rows are empty/partial on C+P open — use saved GET totals.
  const backendSupplyCost = pickFiniteNumber(backendPricing?.totalInputSupplyAmount);
  const backendInstallationCost = pickFiniteNumber(backendPricing?.totalInputInstallationAmount);
  const useBackendSheetCosts =
    (rows.length === 0 || preferBackendPricing || forceBackendSheetCosts) &&
    (backendSupplyCost != null || backendInstallationCost != null);
  const supplyCost = useBackendSheetCosts && backendSupplyCost != null
    ? backendSupplyCost
    : totals.supplyInputAmount;
  const installationCost = supportsInstallation(quotationType)
    ? (useBackendSheetCosts && backendInstallationCost != null
      ? backendInstallationCost
      : totals.inputInstallationAmount)
    : 0;
  const supplyInstallCost = supplyCost + installationCost;
  const totalCost = supplyInstallCost + pmcTotal;
  const liveOverheadsAmount = totalCost * (parseCurrency(overheadsPercent) / 100);
  const liveNetMarginAmount = totalCost * (parseCurrency(markUpPercent) / 100);
  const liveGrossMarginAmount = liveOverheadsAmount + liveNetMarginAmount;
  const liveCalculatedSellingPrice = totalCost + liveGrossMarginAmount;
  const liveNetProfitPercent = liveCalculatedSellingPrice > 0
    ? (liveNetMarginAmount / liveCalculatedSellingPrice) * 100
    : 0;
  const liveGrossProfitPercent = liveCalculatedSellingPrice > 0
    ? (liveGrossMarginAmount / liveCalculatedSellingPrice) * 100
    : 0;
  // Keep live calc for multiplier seeding even when display prefers backend metrics.
  const calculatedSellingPriceForSeed = liveCalculatedSellingPrice;
  const derivedMultiplier = computeDerivedMultiplier(
    calculatedSellingPriceForSeed,
    supplyCost,
    installationCost
  );
  // Project selling with the current derived multiplier for non-manual rows so Realized
  // Gross Margin tracks overheads / mark-up / PMC immediately (same behavior as before).
  const projectedSellingTotal = derivedMultiplier > 0
    ? computeWorkingTotals(
      rows.map((row) => {
        if (isDiscountPercentAdjustment(row.adjustmentType) || row.adjustmentRateManual) {
          return row;
        }
        return {
          ...row,
          adjustmentType: "Multiplier",
          adjustmentRate: formatInputNumber(Number(derivedMultiplier.toFixed(4))),
          multiplier: formatInputNumber(Number(derivedMultiplier.toFixed(4))),
          unitRate: "",
          ...(supportsInstallation(quotationType) && !row.installationMultiplierManual
            ? { installationMultiplier: formatInputNumber(Number(derivedMultiplier.toFixed(4))) }
            : {}),
        };
      }),
      quotationType
    ).customerSellingTotal
    : totals.customerSellingTotal;
  const liveRealizedSellingTotal = projectedSellingTotal > 0 ? projectedSellingTotal : totals.customerSellingTotal;
  const liveRealizedMarginPercent = liveRealizedSellingTotal > 0
    ? ((liveRealizedSellingTotal - totalCost) / liveRealizedSellingTotal) * 100
    : 0;
  const useBackendPricing = preferBackendPricing && hasBackendPricingMetrics(backendPricing);
  // Incomplete lazy systems make projected selling from `rows` undercount — prefer GET metrics.
  const useBackendRealized =
    (preferBackendPricing || forceBackendSheetCosts) && hasBackendPricingMetrics(backendPricing);
  const backendRealizedSellingTotal = resolveBackendRealizedSellingTotal(backendPricing, quotationType);
  const overheadsAmount = useBackendPricing && backendPricing.overheadsAmount != null
    ? backendPricing.overheadsAmount
    : liveOverheadsAmount;
  const netMarginAmount = useBackendPricing && backendPricing.netProfit != null
    ? backendPricing.netProfit
    : liveNetMarginAmount;
  const grossMarginAmount = useBackendPricing && backendPricing.grossProfit != null
    ? backendPricing.grossProfit
    : liveGrossMarginAmount;
  const calculatedSellingPrice = useBackendPricing && backendPricing.calculatedSellingPrice != null
    ? backendPricing.calculatedSellingPrice
    : liveCalculatedSellingPrice;
  const netProfitPercent = useBackendPricing && backendPricing.netMarginPct != null
    ? backendPricing.netMarginPct
    : liveNetProfitPercent;
  const grossProfitPercent = useBackendPricing && backendPricing.grossMarginPct != null
    ? backendPricing.grossMarginPct
    : liveGrossProfitPercent;
  // Prefer supply + installation selling totals from GET so SITC/SERVICE realised selling is complete
  // without requiring every System to be expanded.
  const realizedSellingTotal = (() => {
    if (backendRealizedSellingTotal != null) {
      if (useBackendRealized) return backendRealizedSellingTotal;
      // Guard against undercount when live rows omit collapsed-system installation lines.
      if (
        supportsInstallation(quotationType) &&
        liveRealizedSellingTotal < backendRealizedSellingTotal
      ) {
        return backendRealizedSellingTotal;
      }
    }
    return liveRealizedSellingTotal;
  })();
  const realizedMarginPercent =
    useBackendRealized &&
    backendPricing.realizedGrossMargin != null &&
    pickFiniteNumber(backendPricing.realizedSellingTotal) === realizedSellingTotal
      ? backendPricing.realizedGrossMargin
      : (realizedSellingTotal > 0
        ? ((realizedSellingTotal - totalCost) / realizedSellingTotal) * 100
        : liveRealizedMarginPercent);
  const targetMargin = Number(netPricePercentage) || 0;
  const showMarginWarning = targetMargin > 0 && realizedMarginPercent < targetMargin;
  const seededMultiplier = derivedMultiplier > 0 ? formatInputNumber(Number(derivedMultiplier.toFixed(4))) : "";

  useEffect(() => {
    if (!editable || !setRows || !seededMultiplier) return;

    setRows((current) => {
      let changed = false;
      const next = current.map((row) => {
        const isDiscount = String(row.adjustmentType || "Multiplier") === "Discount %";
        const currentMultiplier = formatMultiplierFieldValue(row.adjustmentRate ?? row.multiplier);
        const currentInstallMultiplier = formatMultiplierFieldValue(row.installationMultiplier);
        const cpMultiplierChanged = !multipliersApproximatelyEqual(row.cpMultiplier, seededMultiplier);
        // Refill only multipliers that are still following C+P. Saved edits stay as saved.
        const needsSupplyUpdate =
          !isDiscount &&
          multiplierValueFollowsCostPricing(currentMultiplier, row.cpMultiplier, {
            isManual: Boolean(row.adjustmentRateManual),
            nextDerived: seededMultiplier,
          }) &&
          !multipliersApproximatelyEqual(currentMultiplier, seededMultiplier);
        const needsInstallUpdate =
          supportsInstallation(quotationType) &&
          multiplierValueFollowsCostPricing(currentInstallMultiplier, row.cpMultiplier, {
            isManual: Boolean(row.installationMultiplierManual),
            nextDerived: seededMultiplier,
          }) &&
          !multipliersApproximatelyEqual(currentInstallMultiplier || 0, seededMultiplier);
        if (!needsSupplyUpdate && !needsInstallUpdate && !cpMultiplierChanged) return row;
        changed = true;
        return {
          ...row,
          // Keep C+P derived as reference even for manual rows (restore on Discount → Multiplier).
          cpMultiplier: seededMultiplier,
          ...(needsSupplyUpdate
            ? {
                adjustmentType: "Multiplier",
                adjustmentRate: seededMultiplier,
                adjustmentRateManual: false,
                multiplier: seededMultiplier,
                // Recalculate Selling Supply from Input × Derived Mult unless user edited unit rate.
                ...(row.unitRateManual
                  ? {}
                  : { unitRate: "", unitRateManual: false }),
                ...CLEARED_BACKEND_AMOUNT_FIELDS,
              }
            : isDiscount
              ? {
                  // Preserve C+P multiplier for restore when switching back from Discount.
                  multiplier: seededMultiplier,
                }
              : {}),
          ...(needsInstallUpdate
            ? {
                installationMultiplier: seededMultiplier,
                installationMultiplierManual: false,
                ...CLEARED_BACKEND_AMOUNT_FIELDS,
              }
            : {}),
        };
      });
      return changed ? next : current;
    });
  }, [
    editable,
    setRows,
    seededMultiplier,
    overheadsPercent,
    markUpPercent,
    pmcTotal,
    supplyCost,
    installationCost,
    quotationType,
  ]);

  const costRows = [
    { head: "Supply", amount: formatCurrency(supplyCost), percent: `${totalCost ? ((supplyCost / totalCost) * 100).toFixed(1) : "0.0"}%` },
    { head: "Installation", amount: formatCurrency(installationCost), percent: `${totalCost ? ((installationCost / totalCost) * 100).toFixed(1) : "0.0"}%` },
    { head: "Total", amount: formatCurrency(supplyInstallCost), percent: `${totalCost ? ((supplyInstallCost / totalCost) * 100).toFixed(0) : "0"}%`, emphasis: true },
    ...(hasPmcConfigs
      ? [
          ...pmcGroups.map((head) => ({ head: head.title, amount: formatCurrency(head.totalCost), percent: `${totalCost ? ((head.totalCost / totalCost) * 100).toFixed(0) : "0"}%` })),
          { head: "Total PMC Cost", amount: formatCurrency(pmcTotal), percent: `${totalCost ? ((pmcTotal / totalCost) * 100).toFixed(0) : "0"}%`, emphasis: true },
        ]
      : []),
    { head: "Total Cost", amount: formatCurrency(totalCost), percent: "100%", grand: true },
  ];

  function updatePmcEntry(entryId, fieldKey, value) {
    if (!setPmcEntries) return;
    setPmcEntries((current) => patchPmcEntryValue(current, entryId, fieldKey, value));
  }

  const isMobileLayout = useIsMobileLayout();

  const systemBreakdown = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const name = String(row.systemName || "").trim() || sq("mobileUngroupedSystem");
      const prepared = computePreparedWorkingRow(row, quotationType);
      const sell =
        Number(prepared.numericTotals.sellingSupplyAmount) +
        (supportsInstallation(quotationType)
          ? Number(prepared.numericTotals.sellingInstallationAmount)
          : 0);
      map.set(name, (map.get(name) || 0) + sell);
    });
    return Array.from(map.entries()).map(([name, amount]) => ({ name, amount }));
  }, [rows, quotationType]);

  if (isMobileLayout) {
    return (
      <QuotationCostPricingMobile
        editable={editable}
        formatMoney={formatCurrency}
        formatMoneyWhole={formatCurrencyWhole}
        localizedFieldProps={localizedFieldProps}
        calculatedSellingPrice={calculatedSellingPrice}
        realizedSellingTotal={realizedSellingTotal}
        difference={Number(realizedSellingTotal || 0) - Number(calculatedSellingPrice || 0)}
        derivedMultiplier={derivedMultiplier}
        realizedMarginPercent={realizedMarginPercent}
        showMarginWarning={showMarginWarning}
        targetMargin={targetMargin}
        systemBreakdown={systemBreakdown}
        overheadsPercent={overheadsPercent}
        setOverheadsPercent={setOverheadsPercent}
        markUpPercent={markUpPercent}
        setMarkUpPercent={setMarkUpPercent}
        totalCost={totalCost}
        overheadsAmount={overheadsAmount}
        netMarginAmount={netMarginAmount}
        netProfitPercent={netProfitPercent}
        grossMarginAmount={grossMarginAmount}
        grossProfitPercent={grossProfitPercent}
        hasPmcConfigs={hasPmcConfigs}
        costRows={costRows}
        pmcGroups={pmcGroups}
        onUpdatePmcEntry={editable ? updatePmcEntry : undefined}
      />
    );
  }

  function renderPmcCell(row, column) {
    // Prefer the column's own field — do not let empty `rate` hide BOQ `unitRate` defaults.
    const rawValue = column.key === "unitRate"
      ? (row.unitRate ?? "")
      : column.key === "rate"
        ? (row.rate ?? row.ctc ?? "")
        : column.key === "totalCost"
          ? (row.totalCost ?? row.amount ?? "")
          : (row[column.key] ?? "");
    const value = rawValue === 0 ? "0" : String(rawValue ?? "");

    if (column.key === "totalCost" && (column.computed || column.readonly)) {
      return (
        <span className="block text-right text-[12px] font-semibold text-[#2d394c]">
          {formatCurrency(row.totalCost)}
        </span>
      );
    }
    if (column.key === "description") {
      return (
        <span className="block text-left text-[12px] font-medium text-[#2d394c]">
          {row.lineName || row.description || "-"}
        </span>
      );
    }
    if (!editable || column.readonly || !setPmcEntries) {
      return (
        <span className={`block text-[12px] text-[#2d394c] ${column.align === "left" ? "text-left" : "text-right"}`}>
          {column.key === "totalCost" ? formatCurrency(row.totalCost) : (value || "-")}
        </span>
      );
    }
    return (
      <input
        value={value}
        onChange={(event) => updatePmcEntry(row.id, column.key, event.target.value)}
        disabled={!editable}
        inputMode="decimal"
        {...localizedFieldProps()}
        className={`h-7 w-full min-w-[56px] rounded-[6px] border border-transparent bg-transparent px-1.5 text-[12px] text-[#2d394c] outline-hidden transition hover:border-[#d5deec] focus:border-[#9fb4dd] focus:bg-white disabled:text-[#6f82a1] ${column.align === "left" ? "text-left" : "text-right"}`}
      />
    );
  }

  return (
    <section className="grid gap-3 xl:grid-cols-[1.35fr_1fr]">
      <div className="space-y-3">
        <SectionShell compact title="Cost Details" action={<span className="rounded-[10px] bg-[#fff2c9] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-[#e28a00]">Internal only</span>}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] border-collapse">
              <thead>
                <tr className="border-b border-[#d9e1ee] bg-[#f8fbff]">
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[#96a7c2]">Head</th>
                  <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-[#96a7c2]">Amount</th>
                  <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-[#96a7c2]">% of Cost</th>
                </tr>
              </thead>
              <tbody>
                {costRows.map((row) => (
                  <tr key={row.head} className="border-b border-[#edf1f7] last:border-b-0">
                    <td className={`px-3 py-2 text-left text-[12px] ${row.grand ? "font-semibold text-[#2944a8]" : row.emphasis ? "font-semibold text-[#2d394c]" : "text-[#2d394c]"}`}>{row.head}</td>
                    <td className={`px-3 py-2 text-right text-[12px] ${row.grand ? "font-semibold text-[#2944a8]" : row.emphasis ? "font-semibold text-[#2d394c]" : "text-[#6f82a1]"}`}>{row.amount}</td>
                    <td className={`px-3 py-2 text-right text-[12px] ${row.grand ? "font-semibold text-[#2944a8]" : row.emphasis ? "font-semibold text-[#2d394c]" : "text-[#6f82a1]"}`}>{row.percent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionShell>

        {hasPmcConfigs && pmcGroups.length > 0 && (
          <div className="space-y-3">
            {pmcGroups.map((head) => (
              <div
                key={head.id}
                className="overflow-hidden rounded-[12px] border border-[#dde5f1] bg-white shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)]"
              >
                <div className="flex items-center justify-between border-b border-[#d9e1ee] px-4 py-3 sm:px-5">
                  <h3 className="text-[13px] font-semibold tracking-[-0.01em] text-[#2d394c]">{head.title}</h3>
                  <span className="text-[13px] font-semibold text-[#23324c]">{formatCurrency(head.totalCost)}</span>
                </div>
                {head.columns.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] border-collapse">
                      <thead>
                        <tr className="border-b border-[#d9e1ee] bg-[#f8fbff]">
                          {head.columns.map((column) => (
                            <th
                              key={column.reactKey || column.id || column.key}
                              className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#96a7c2] ${column.align === "left" ? "text-left" : "text-right"}`}
                            >
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {head.rows.map((row, index) => (
                          <tr key={`${head.id}-${row.id || index}`} className="border-b border-[#edf1f7] last:border-b-0">
                            {head.columns.map((column) => (
                              <td
                                key={`${head.id}-${row.id || index}-${column.reactKey || column.id || column.key}`}
                                className={`px-4 py-2 ${column.align === "left" ? "text-left" : "text-right"}`}
                              >
                                {renderPmcCell(row, column)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : head.rows?.length ? (
                  <div className="divide-y divide-[#edf1f7]">
                    {head.rows.map((row, index) => (
                      <div key={`${head.id}-${row.id || index}`} className="px-4 py-2.5 text-[12px] text-[#2d394c]">
                        {row.lineName || row.description || "-"}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-[12px] text-[#7a8da8]">
                    No active line items for this cost head.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <SectionShell compact title="Selling Details">
          <div className="px-3 py-2.5 sm:px-4">
            <div className="flex items-center justify-between border-b border-[#edf1f7] py-2.5">
              <p className="text-[12px] text-[#6f82a1]">Overheads</p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <input
                    value={overheadsPercent}
                    onChange={(event) => setOverheadsPercent?.(event.target.value)}
                    disabled={!editable}
                    inputMode="decimal"
                    maxLength={5}
                    {...localizedFieldProps()}
                    className="h-8 w-12 shrink-0 rounded-[8px] border border-[#cdd8ea] bg-white px-1.5 text-right text-[12px] text-[#5c6f90] outline-hidden disabled:bg-[#fbfcfe]"
                  />
                  <span className="text-[12px] text-[#94a5bf]">%</span>
                </div>
                <p className="min-w-[100px] text-right text-[13px] font-semibold text-[#23324c]">{formatCurrency(roundExcelWhole(overheadsAmount))}</p>
              </div>
            </div>
            <div className="flex items-center justify-between border-b border-[#edf1f7] py-2.5">
              <p className="text-[12px] text-[#6f82a1]">Mark Up</p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <input
                    value={markUpPercent}
                    onChange={(event) => setMarkUpPercent?.(event.target.value)}
                    disabled={!editable}
                    inputMode="decimal"
                    maxLength={5}
                    {...localizedFieldProps()}
                    className="h-8 w-12 shrink-0 rounded-[8px] border border-[#cdd8ea] bg-white px-1.5 text-right text-[12px] text-[#5c6f90] outline-hidden disabled:bg-[#fbfcfe]"
                  />
                  <span className="text-[12px] text-[#94a5bf]">%</span>
                </div>
                <p className="min-w-[100px] text-right text-[13px] font-semibold text-[#94a5bf]">-</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 border-b border-[#edf1f7] py-2.5">
              <p className="text-[12px] text-[#6f82a1]">
                Net Profit{" "}
                <span className="text-[#9aacbf]">
                  (Net Margin{"\u00A0"}
                  <span className="font-semibold text-[#23324c]">{netProfitPercent.toFixed(1)}%</span>)
                </span>
              </p>
              <p className="shrink-0 text-[13px] font-semibold text-[#23324c]">{formatCurrency(roundExcelWhole(netMarginAmount))}</p>
            </div>
            <div className="flex items-center justify-between gap-3 border-b border-[#edf1f7] py-2.5">
              <p className="text-[12px] text-[#6f82a1]">
                Gross Profit{" "}
                <span className="text-[#9aacbf]">
                  (Gross Margin{"\u00A0"}
                  <span className="font-semibold text-[#23324c]">{grossProfitPercent.toFixed(1)}%</span>)
                </span>
              </p>
              <p className="shrink-0 text-[13px] font-semibold text-[#23324c]">{formatCurrency(roundExcelWhole(grossMarginAmount))}</p>
            </div>
            <div className="flex items-center justify-between pt-3">
              <p className="text-[13px] font-semibold text-[#2d394c]">Calculated Selling Price</p>
              <p className="text-[18px] font-semibold tracking-[-0.03em] text-[#2944a8]">{formatCurrency(calculatedSellingPrice)}</p>
            </div>
          </div>
        </SectionShell>

        <SectionShell compact title="Realized Pricing Summary">
          <div className="px-3 py-2.5 sm:px-4">
            <SummaryRow
              label="Derived multiplier"
              helper="seeded to Working Sheet Mult/Disc Rate"
              value={derivedMultiplier > 0 ? `${derivedMultiplier.toFixed(2)}x` : "-"}
            />
            <SummaryRow label="Realized selling" helper="from Working Sheet" value={formatCurrency(realizedSellingTotal)} />
            <SummaryRow
              label="Difference"
              helper="Realized Selling − Calculated Selling Price"
              value={formatCurrency(Number(realizedSellingTotal || 0) - Number(calculatedSellingPrice || 0))}
            />
            <SummaryRow
              label="Realized Gross margin"
              value={`${realizedMarginPercent.toFixed(1)}%`}
              highlight={showMarginWarning}
            />
            {showMarginWarning && (
              <div className="mt-2 rounded-[10px] bg-[#fff3cd] px-3 py-2.5 text-[12px] leading-5 text-[#d47700]">
                Realized Gross Margin is below the tenant target of {targetMargin}%. This warning should stay visible during manager review.
              </div>
            )}
          </div>
        </SectionShell>
      </div>
    </section>
  );
}

export {
  CostPricingTab
};
